/**
 * Entry point.
 *   URL ?gamekey= → GameState(variant) → BetPanel + Topbar + LotteryMachine
 *   → GameOrchestrator.start()
 *
 * The 3D `LotteryMachine` is the vendored library at src/lib/lottery-machine
 * (originally the AMGLiveBall prototype). It loads `public/models/*.glb`
 * + `public/hdri/*.hdr` and emits byte-accurate progress events while it
 * streams the GLB so we can drive the splash bar.
 */
import { Api } from './net/api';
import { GameState } from './game/gameState';
import { GameOrchestrator } from './game/gameOrchestrator';
import { MultiplayerOrchestrator } from './game/multiplayerOrchestrator';
import { loadUrlParams } from './game/urlParams';
import { getVariant, variantLabel } from './game/variants';
import { LotteryMachine } from './lib/lottery-machine';
import type { GameId } from './lib/lottery-machine';
import type { GameKey } from './types/game';
import { BetPanel } from './ui/betPanel';
import { Topbar } from './ui/topbar';
import { debugLog, error as logError } from './util/logger';

/** Common surface both orchestrators expose to UI handlers. */
interface OrchestratorLike {
  start(): Promise<void>;
  tryPlaceBet(betKey: string): void;
  selectChip(index: number): void;
  clearAll(): void;
  repeatLast(): void;
  confirm(): Promise<void>;
}

/** Map our BAL game keys to the 3D library's profile IDs. */
const PROFILE_BY_GAME_KEY: Record<GameKey, GameId> = {
  BAL6: 'ball-6',
  BAL12: 'ball-12',
  BAL24: 'ball-24',
  BAL48: 'ball-48',
};

async function bootstrap(): Promise<void> {
  if (!('WebGL2RenderingContext' in window)) {
    showFatal('Your browser does not support WebGL2.');
    return;
  }

  const params = loadUrlParams();
  debugLog('[main] URL params', params);

  const topbar = new Topbar();
  const variant = getVariant(params.gameKey);
  const mpSuffix = params.multiplayer ? ' (Multi)' : '';
  topbar.setGame(variantLabel(params.gameKey) + mpSuffix);
  topbar.setSplashProgress(0.05, `Booting ${variantLabel(params.gameKey)}${mpSuffix}…`);

  const state = new GameState(variant);
  const api = new Api();

  // Build the betting UI first — instant, makes the page feel responsive.
  const panel = new BetPanel({
    onBet: (key) => orchestratorRef?.tryPlaceBet(key),
    onSelectChip: (i) => orchestratorRef?.selectChip(i),
    onBatal: () => orchestratorRef?.clearAll(),
    onUlang: () => orchestratorRef?.repeatLast(),
    onPasang: () => void orchestratorRef?.confirm(),
  });
  panel.renderVariant(variant);
  panel.bindChips(state.config);
  panel.setSelectedChip(state.selectedChipIndex);
  panel.setBettingEnabled(false);
  panel.setMode(params.multiplayer ? 'multi' : 'single');

  // 3D chamber — instantiates a WebGL canvas inside #machine-canvas-host and
  // streams its GLB + HDRI assets. loadProgress lets us drive the splash.
  const host = document.getElementById('machine-canvas-host') as HTMLElement;
  const machine = new LotteryMachine(host, {
    game: PROFILE_BY_GAME_KEY[params.gameKey],
    autoStart: true,
  });

  machine.onEvent((evt) => {
    switch (evt.type) {
      case 'loadProgress':
        if (evt.phase === 'download') {
          // Reserve 0.85 of the bar for the download — the prepare phase
          // fills the last sliver.
          const ratio = evt.total > 0 ? evt.ratio : 0;
          topbar.setSplashProgress(0.1 + ratio * 0.75, 'Streaming chamber assets…');
        } else {
          topbar.setSplashProgress(0.92, 'Calibrating orbs…');
        }
        break;
      case 'ready':
        topbar.setSplashProgress(1, 'Ready');
        window.setTimeout(() => topbar.dismissSplash(), 250);
        break;
      case 'error':
        logError('[machine] error', evt.error);
        showFatal(`Failed to load chamber: ${evt.error.message}`);
        break;
      default:
        break;
    }
  });

  try {
    await machine.init();
  } catch (e) {
    logError('[main] machine.init failed', e);
    showFatal(`Failed to load chamber: ${(e as Error)?.message ?? String(e)}`);
    return;
  }

  // Orchestrator drives the round loop. MP and single-player implementations
  // share an interface; main.ts picks based on the URL flag.
  const orchestrator: OrchestratorLike = params.multiplayer
    ? new MultiplayerOrchestrator({ state, api, machine, panel, topbar, params })
    : new GameOrchestrator({ state, api, machine, panel, topbar, params });
  let orchestratorRef: OrchestratorLike | null = orchestrator;

  // Dev-only debug handle.
  (window as unknown as { __liveball?: { state: GameState; orchestrator: OrchestratorLike } }).__liveball = {
    state,
    orchestrator,
  };

  await orchestrator.start();
}

function showFatal(message: string): void {
  const overlay = document.getElementById('fatal-overlay');
  const msg = document.getElementById('fatal-message');
  if (msg) msg.textContent = message;
  if (overlay) overlay.classList.add('visible');
}

window.addEventListener('error', (e) => logError('[window.error]', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) => logError('[unhandledrejection]', e.reason));

bootstrap().catch((e) => {
  logError('[main] bootstrap crashed', e);
  showFatal(`Bootstrap failed: ${(e as Error)?.message ?? String(e)}`);
});
